import React, { ReactNode, useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { Icons } from '@ohif/ui-next';
import ToggleSwitch from './ToggleSwitch';
import { DATA_TABLE_ALIGN_MAP, DATA_TABLE_HEADER_ALIGN_MAP } from '../../constants';
import './DataTable.css';

export interface TableColumn<T = unknown> {
  key: string;
  label: string;
  width?: string | number;
  /** When set, column grows to fill remaining table width (min width from `width`). */
  flex?: number;
  render?: (value: unknown, row: T, index: number) => ReactNode;
  align?: 'left' | 'center' | 'right';
}

interface DataTableProps<T = unknown> {
  columns: TableColumn<T>[];
  data: T[];
  onRowClick?: (row: T, index: number) => void;
  onEdit?: (row: T, index: number) => void;
  onToggleActive?: (row: T, index: number, checked: boolean) => void;
  getRowKey?: (row: T, index: number) => string | number;
  fixedRightColumnKey?: string;
  /** Split header/body horizontal scroll so the header can use page scroll (overflow-x:auto traps position:sticky). */
  pageScrollStickyHeader?: boolean;
  /** Stretch rows/header to 100% width; pair with column `flex` for fluid layouts. */
  fullWidth?: boolean;
}

function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  onRowClick,
  onEdit,
  onToggleActive,
  getRowKey = (_, i) => i,
  fixedRightColumnKey,
  pageScrollStickyHeader = false,
  fullWidth = false,
}: Readonly<DataTableProps<T>>) {
  const isFixedRight = useCallback(
    (key: string) => fixedRightColumnKey !== undefined && key === fixedRightColumnKey,
    [fixedRightColumnKey]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const syncScrollLockRef = useRef(false);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  /** Body vertical scrollbar width — pad sticky header strip so Actions column aligns with body at any zoom. */
  const [stickyHeaderEndGutterPx, setStickyHeaderEndGutterPx] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const check = () => {
      setHasHorizontalScroll(el.scrollWidth > el.clientWidth);
      if (pageScrollStickyHeader) {
        setStickyHeaderEndGutterPx(Math.round(Math.max(0, el.offsetWidth - el.clientWidth)));
      } else {
        setStickyHeaderEndGutterPx(0);
      }
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener('resize', check);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [data.length, columns.length, pageScrollStickyHeader]);

  const syncHeaderScrollFromBody = useCallback(() => {
    const body = scrollRef.current;
    const head = headerScrollRef.current;
    if (!body || !head || syncScrollLockRef.current) {
      return;
    }
    syncScrollLockRef.current = true;
    head.scrollLeft = body.scrollLeft;
    syncScrollLockRef.current = false;
  }, []);

  const syncBodyScrollFromHeader = useCallback(() => {
    const body = scrollRef.current;
    const head = headerScrollRef.current;
    if (!body || !head || syncScrollLockRef.current) {
      return;
    }
    syncScrollLockRef.current = true;
    body.scrollLeft = head.scrollLeft;
    syncScrollLockRef.current = false;
  }, []);

  const fixedColDimensionStyle = useCallback(
    (column: TableColumn<T>) => {
      const w = column.width ?? 150;
      if (column.flex !== undefined) {
        return { flex: `${column.flex} 1 0%`, minWidth: w, width: 'auto' as const };
      }
      return isFixedRight(column.key)
        ? { width: w, minWidth: w, maxWidth: w }
        : { width: w, minWidth: w, flexShrink: 0 };
    },
    [isFixedRight]
  );

  const renderCell = useCallback(
    (column: TableColumn<T>, row: T, rowIndex: number) => {
      const value = row[column.key];
      const content = column.render ? column.render(value, row, rowIndex) : value;
      const align = column.align ? (DATA_TABLE_ALIGN_MAP[column.align] ?? '') : '';
      const fixed = isFixedRight(column.key) ? 'data-table-cell-fixed-right' : '';

      if (column.key === 'active' && onToggleActive) {
        const activeW = column.width ?? 80;
        const activeDim = isFixedRight(column.key)
          ? { width: activeW, minWidth: activeW, maxWidth: activeW }
          : { width: activeW };
        return (
          <div
            key={column.key}
            className={['data-table-cell', 'data-table-cell-active', align, fixed]
              .filter(Boolean)
              .join(' ')}
            style={activeDim}
            onClick={e => e.stopPropagation()}
          >
            <ToggleSwitch
              checked={Boolean(value)}
              onChange={c => onToggleActive(row, rowIndex, c)}
            />
          </div>
        );
      }

      const isReact = React.isValidElement(content);
      const wrapperClass =
        column.align === 'center'
          ? 'data-table-cell-content-wrapper-center'
          : column.align === 'right'
            ? 'data-table-cell-content-wrapper-right'
            : '';
      return (
        <div
          key={column.key}
          className={['data-table-cell', align, isReact ? 'data-table-cell-visible' : '', fixed]
            .filter(Boolean)
            .join(' ')}
          style={fixedColDimensionStyle(column)}
        >
          {isReact ? (
            <div className={`data-table-cell-content-wrapper ${wrapperClass}`.trim()}>
              {content}
            </div>
          ) : (
            <div
              className="data-table-cell-text"
              title={typeof content === 'string' ? content : undefined}
            >
              {content}
            </div>
          )}
        </div>
      );
    },
    [onToggleActive, isFixedRight, fixedColDimensionStyle]
  );

  const headerCells = useMemo(
    () =>
      columns.map(col => (
        <div
          key={col.key}
          className={[
            'data-table-header-cell',
            col.align ? (DATA_TABLE_HEADER_ALIGN_MAP[col.align] ?? '') : '',
            isFixedRight(col.key) ? 'data-table-header-cell-fixed-right' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={fixedColDimensionStyle(col)}
        >
          {col.label}
        </div>
      )),
    [columns, fixedColDimensionStyle, isFixedRight]
  );

  const hasData = data.length > 0;
  const rows = hasData
    ? data.map((row, rowIndex) => (
        <div
          key={getRowKey(row, rowIndex)}
          className={['data-table-row', onRowClick ? 'data-table-row-clickable' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => onRowClick?.(row, rowIndex)}
        >
          {columns.map(col => renderCell(col, row, rowIndex))}
          {onEdit && (
            <div
              className="data-table-edit-cell"
              onClick={e => {
                e.stopPropagation();
                onEdit(row, rowIndex);
              }}
            >
              <button
                type="button"
                className="data-table-edit-button"
                aria-label="Edit"
              >
                <Icons.EditButton
                  width={16}
                  height={16}
                />
              </button>
            </div>
          )}
        </div>
      ))
    : null;

  const hScrollClass = hasHorizontalScroll ? ' data-table-has-horizontal-scroll' : '';
  const fullWidthClass = fullWidth ? ' data-table-full-width' : '';

  if (pageScrollStickyHeader) {
    return (
      <div className={`data-table-container data-table-page-sticky${fullWidthClass}`}>
        <div className="data-table-page-sticky-header-shell">
          <div
            ref={headerScrollRef}
            className={`data-table-header-h-scroll${hScrollClass}`}
            style={
              stickyHeaderEndGutterPx > 0
                ? { paddingInlineEnd: stickyHeaderEndGutterPx }
                : undefined
            }
            onScroll={syncBodyScrollFromHeader}
          >
            <div className="data-table-header">
              {headerCells}
              {onEdit && <div className="data-table-header-spacer" />}
            </div>
          </div>
        </div>
        <div className="data-table-scroll-container data-table-page-sticky-body-wrap">
          <div
            ref={scrollRef}
            className={`data-table-body-h-scroll${hScrollClass}`}
            onScroll={syncHeaderScrollFromBody}
          >
            <div className="data-table-body data-table-page-sticky-body-inner">
              {hasData ? rows : <div className="data-table-empty-state">No Data Found</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`data-table-container${fullWidthClass}`}>
      <div className="data-table-scroll-container">
        <div
          ref={scrollRef}
          className={`data-table-body-scroll${hScrollClass}`}
        >
          <div className="data-table-inner-container">
            <div className="data-table-header">
              {headerCells}
              {onEdit && <div className="data-table-header-spacer" />}
            </div>
            <div className="data-table-body">
              {hasData ? rows : <div className="data-table-empty-state">No Data Found</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataTable;
